import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { OrgFinancingProfile } from '@hero/schema';
import { useOrgProfileUpdateApi } from '@hero/api-hooks';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@hero/web-components';
import { ProfileFieldset } from '../ApplyForm/ProfileFieldset';
import type { ApplyFormValues } from '../ApplyForm/types';
import {
  isFinancingProfileDraftValid,
  profileToFormValues,
} from '../../utils/financingProfile';

export interface FinancingProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Latest saved profile, projected off the unified
   * `useOrgProfileApi()` response (`.financingProfile`).
   */
  savedProfile: OrgFinancingProfile | null | undefined;
  /** Fired after a successful save with the freshly-stamped profile. */
  onSaved?: (profile: OrgFinancingProfile) => void;
}

/**
 * Drawer editor for `Org.financingProfile`. Mirrors the legal/banking
 * drawer pattern on the Terms step — the profile step shows a
 * RequirementsCard; this sheet holds the actual form.
 *
 * Writes go through the unified `PATCH /org/profile` endpoint via
 * `useOrgProfileUpdateApi` (section-keyed body
 * `{ financingProfile: {...} }`). The dedicated
 * `PATCH /financing/profile` endpoint is gone.
 */
export function FinancingProfileDrawer({
  isOpen,
  onClose,
  savedProfile,
  onSaved,
}: FinancingProfileDrawerProps) {
  const { t } = useTranslation('m-financing');
  const updateOrgProfile = useOrgProfileUpdateApi();

  const [values, setValues] = useState<ApplyFormValues>(() => ({
    ...emptyDraft(),
    ...profileToFormValues(savedProfile),
  }));

  useEffect(() => {
    if (isOpen) {
      setValues(prev => ({
        ...prev,
        ...profileToFormValues(savedProfile),
      }));
    }
  }, [isOpen, savedProfile]);

  const onChange = <K extends keyof ApplyFormValues>(
    key: K,
    value: ApplyFormValues[K],
  ) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!isFinancingProfileDraftValid(values)) {
      toast.error(t('apply_form.profile.save.invalid'));
      return;
    }
    try {
      const financingProfile: OrgFinancingProfile = {
        fleetTier: values.fleetTier || 'lt_10',
        annualRevenueTier: values.annualRevenueTier || 'lt_1m',
        financingNeeds: values.financingNeeds,
        ownsAllTrucks: values.ownsAllTrucks === 'yes',
        ...(values.ownsAllTrucks === 'no' && {
          trucksOwnedCount: Number(values.trucksOwnedCount),
        }),
        hasLoans: values.hasLoans === 'yes',
        ...(values.hasLoans === 'yes' && {
          monthlyFinancingPayment: Number(values.monthlyFinancingPayment),
        }),
        ...(values.isProfitable !== '' && {
          isProfitable: values.isProfitable === 'yes',
        }),
      };
      const result = await updateOrgProfile.mutateAsync({
        financingProfile,
      });
      toast.success(t('apply_form.profile.save.success'));
      onSaved?.(result.financingProfile ?? financingProfile);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('apply_form.profile.save.error'),
      );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>{t('apply_form.profile.drawer.title')}</SheetTitle>
          <SheetDescription>{t('apply_form.profile.drawer.description')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 p-6">
          <ProfileFieldset values={values} onChange={onChange} />
        </div>

        <SheetFooter className="sticky bottom-0 border-t bg-white px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateOrgProfile.isPending}>
            {t('apply_form.profile.drawer.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              updateOrgProfile.isPending || !isFinancingProfileDraftValid(values)
            }
            className="bg-emerald-700 text-white hover:bg-emerald-800">
            {updateOrgProfile.isPending
              ? t('apply_form.profile.save.saving')
              : t('apply_form.profile.drawer.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function emptyDraft(): ApplyFormValues {
  return {
    fleetTier: '',
    annualRevenueTier: '',
    financingNeeds: [],
    ownsAllTrucks: '',
    trucksOwnedCount: '',
    hasLoans: '',
    monthlyFinancingPayment: '',
    isProfitable: '',
    financialStatements: [],
    invoiceTab: 'external',
    heroInvoiceId: '',
    heroInvoiceAmount: '',
    heroInvoiceCustomerName: '',
    heroInvoiceNumber: '',
    heroExpectedDueDate: null,
    externalClientId: '',
    externalCustomerName: '',
    externalAmount: '',
    externalExpectedDueDate: null,
    externalInvoiceNumber: '',
    externalDocument: null,
    requestedAmount: '',
    requestedAmountSameAsInvoice: true,
    requestedTermKey: '60_days',
  };
}
